import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, analytics } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { logEvent } from 'firebase/analytics';

const PACKAGES = [
  {
    id: 'quick_fix',
    name: 'Quick Fix',
    credits: 1,
    price: 'NZD 1.59',
    description: 'Perfect for a single job application',
    priceId: 'price_1TdJBOJVHYMFlJp7h6y8PquF',
    color: '#0F7DFF',
  },
  {
    id: 'smart_start',
    name: 'Smart Start',
    credits: 5,
    price: 'NZD 5.99',
    description: 'Ideal for an active job search',
    priceId: 'price_1TdJBkJVHYMFlJp7RrflrN05',
    color: '#00D4AA',
  },
  {
    id: 'job_hunter',
    name: 'Job Hunter',
    credits: 20,
    price: 'NZD 22.99',
    description: 'For serious job seekers',
    priceId: 'price_1TdJCFJVHYMFlJp7l9y3o0p5',
    color: '#FFB347',
    popular: true,
  },
  {
    id: 'career_pro',
    name: 'Career Pro',
    credits: 50,
    price: 'NZD 46.99',
    description: 'Maximum value for career changers',
    priceId: 'price_1TdJD5JVHYMFlJp7kN74fjyR',
    color: '#B47FFF',
  },
  {
    id: 'ultimate_edge',
    name: 'Ultimate Edge',
    credits: 100,
    price: 'NZD 69.99',
    description: 'The complete career toolkit',
    priceId: 'price_1TdJDbJVHYMFlJp7AyRpVB6n',
    color: '#FF6B6B',
  },
];

export default function PaywallPage() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const handlePurchase = async (priceId, packageId, packageName, credits) => {
    setLoading(packageId);
    setError('');
    logEvent(analytics, 'purchase_initiated', { package_id: packageId, package_name: packageName, credits });
    try {
      const token = await user.getIdToken();
      const response = await fetch('https://api.theapplyedge.com/api/v1/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ price_id: priceId }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Payment failed.');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(null);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0D0D1A' }}>

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 40px', borderBottom: '1px solid #2A2A4A'
      }}>
        <div onClick={() => navigate('/app')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <img src="/app_icon.png" alt="ApplyEdge" style={{ width: 34, height: 34, borderRadius: 9 }} />
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18 }}>ApplyEdge</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ color: '#8888AA', fontSize: 13 }}>{user?.displayName}</div>
          <button onClick={handleSignOut} style={{
            background: 'transparent', border: '1px solid #2A2A4A',
            borderRadius: 8, padding: '6px 14px', color: '#8888AA',
            fontSize: 13, cursor: 'pointer'
          }}>Sign Out</button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{
          fontFamily: 'Space Grotesk, sans-serif', fontSize: 32,
          fontWeight: 700, marginBottom: 8, textAlign: 'center'
        }}>Get More Credits</h1>
        <p style={{ color: '#8888AA', marginBottom: 40, textAlign: 'center' }}>
          One-time purchase — no subscription, no recurring charges.
        </p>

        {error && (
          <div style={{
            background: 'rgba(255,107,107,0.1)', border: '1px solid #FF6B6B',
            borderRadius: 10, padding: 14, marginBottom: 20, color: '#FF6B6B', fontSize: 14, textAlign: 'center'
          }}>{error}</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          {PACKAGES.map((pkg) => (
            <div key={pkg.id} style={{
              background: '#1A1A2E', border: `1px solid ${pkg.popular ? pkg.color : '#2A2A4A'}`,
              borderRadius: 16, padding: 20, textAlign: 'center', position: 'relative',
              boxShadow: pkg.popular ? `0 0 20px ${pkg.color}33` : 'none'
            }}>
              {pkg.popular && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: pkg.color, borderRadius: 20, padding: '4px 12px',
                  fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap'
                }}>MOST POPULAR</div>
              )}
              <div style={{
                fontSize: 28, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
                color: pkg.color, marginBottom: 4
              }}>{pkg.credits}</div>
              <div style={{ color: '#8888AA', fontSize: 11, marginBottom: 12 }}>
                {pkg.credits === 1 ? 'credit' : 'credits'}
              </div>
              <div style={{
                fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700,
                fontSize: 15, marginBottom: 4
              }}>{pkg.name}</div>
              <div style={{ color: '#8888AA', fontSize: 12, marginBottom: 16, lineHeight: 1.4 }}>
                {pkg.description}
              </div>
              <div style={{
                fontSize: 18, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
                marginBottom: 16
              }}>{pkg.price}</div>
              <button
                onClick={() => handlePurchase(pkg.priceId, pkg.id, pkg.name, pkg.credits)}
                disabled={loading === pkg.id}
                style={{
                  width: '100%', padding: '10px',
                  background: loading === pkg.id ? '#2A2A4A' : `linear-gradient(135deg, ${pkg.color}, ${pkg.color}CC)`,
                  border: 'none', borderRadius: 10, color: '#fff',
                  fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13,
                  cursor: loading === pkg.id ? 'not-allowed' : 'pointer'
                }}>
                {loading === pkg.id ? 'Loading...' : 'Buy Now'}
              </button>
            </div>
          ))}
        </div>

        <p style={{ color: '#555570', fontSize: 12, textAlign: 'center', marginTop: 24 }}>
          Payments are processed securely by Stripe. Credits are added instantly after payment.
        </p>
      </div>
    </div>
  );
}